import React from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { useFormik } from 'formik';

import FormInput from '../../Form/FormInput';
import SectionItemStoreLocatorAddress from '../SectionItems/SectionItemStoreLocatorAddress';
import SectionItemStoreLocatorName from '../SectionItems/SectionItemStoreLocatorName';
import envConfig from '../../../config/env';
import notNullOrUndefined from '../../../utils/misc/notNullOrUndefined';
import { NO_SCROLL_ID } from '../../../config/constants';

import * as S from './styles';

// Types
import { CMS_ENUM_STORE_TYPE } from '../../../../types/global';
import { SectionStoreLocatorContainerQuery_cms_stores } from '../../../containers/Section/SectionStoreLocator/__generated__/SectionStoreLocatorContainerQuery';

const DISTANCE_CUTOFF_IN_METERS = 500000;
const HOME = { lat: 52.5471074, lng: 13.4273716 }; // Home
const PREFFERED_DISTANCE_THRESHOLD_IN_METERS = 200000;

const LOADER = new Loader({
  apiKey: envConfig.googleMapsApiKey,
  libraries: ['geometry'],
  version: 'beta',
});

type StoreWithDistance = SectionStoreLocatorContainerQuery_cms_stores & { distance: number };

type Props = {
  stores: SectionStoreLocatorContainerQuery_cms_stores[];
};

const SectionStoreLocator = ({ stores }: Props) => {
  const gmaps = React.useRef<typeof google.maps | null>(null);
  const [sortedStores, setSortedStores] = React.useState<StoreWithDistance[]>([]);

  const loadGmaps = async () => {
    gmaps.current = await LOADER.load().then(google => google.maps);
  };

  const sortStores = async (coordinates = HOME) => {
    if (!notNullOrUndefined(gmaps.current)) {
      return;
    }

    const mappedStores = stores.map(store => {
      return {
        ...store,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        distance: gmaps.current!.geometry.spherical.computeDistanceBetween(coordinates, { lat: store.lat, lng: store.lng }),
      };
    });
    const sortedStoresWithoutPreference = mappedStores.sort((storeA, storeB) => storeA.distance - storeB.distance);
    const nextSortedStores = sortedStoresWithoutPreference.sort((storeA, storeB) => {
      if (storeA.type === storeB.type) {
        return 0;
      }

      if (storeA.type === CMS_ENUM_STORE_TYPE.SHOWROOM_OR_STORE && storeA.distance < PREFFERED_DISTANCE_THRESHOLD_IN_METERS) {
        return -1;
      }

      if (storeB.type === CMS_ENUM_STORE_TYPE.SHOWROOM_OR_STORE && storeB.distance < PREFFERED_DISTANCE_THRESHOLD_IN_METERS) {
        return 1;
      }

      return 0;
    });

    const filteredStores = nextSortedStores.filter(store => store.distance <= DISTANCE_CUTOFF_IN_METERS);

    if (filteredStores.length === 0) {
      setSortedStores(nextSortedStores.slice(0, 10));
    } else {
      setSortedStores(filteredStores);
    }
  };

  const { handleBlur, handleChange, handleSubmit, values } = useFormik({
    initialValues: { search: '' },
    onSubmit: async formValues => {
      if (!notNullOrUndefined(gmaps.current)) {
        return;
      }

      const geocoder = new gmaps.current.Geocoder();

      geocoder.geocode({ address: formValues.search }, result => {
        if (!notNullOrUndefined(result) || result.length === 0) {
          return;
        }

        const [firstResult] = result;

        sortStores(firstResult.geometry.location.toJSON());
      });
    },
  });

  React.useEffect(() => {
    loadGmaps();
  }, []);

  return (
    <S.Section
      backgroundColor="#606060"
      contentPositionX="middle"
      contentPositionY="center"
      isHiddenOnMobile={false}
      isNeighborClipped={false}
      isNotClipable={true}
      sectionWidth={100}
    >
      <S.Wrapper>
        <S.Headline>Store Locator</S.Headline>

        <S.Form noValidate={true} onSubmit={handleSubmit}>
          <FormInput
            disabled={false}
            name="search"
            onBlur={handleBlur}
            onChange={handleChange}
            placeholder="Enter your address, city, or postal code."
            required={true}
            type="text"
            value={values.search}
          />
        </S.Form>
        {
          // @ts-ignore
          <S.StoreList id={NO_SCROLL_ID}>
            {sortedStores.map(store => (
              <S.StoreItem id={NO_SCROLL_ID} key={store.id}>
                <SectionItemStoreLocatorName name={store.nameDisplay} />

                <SectionItemStoreLocatorAddress
                  address={store.address}
                  continent={store.continent}
                  country={store.country}
                  email={store.email}
                  isAppointmentOnly={store.appointmentOnly || false}
                  phone={store.phone}
                  website={store.website}
                />
              </S.StoreItem>
            ))}
          </S.StoreList>
        }
      </S.Wrapper>
    </S.Section>
  );
};

export default SectionStoreLocator;

// map pin icon
